import {
  API_VERSION_HEADER,
  getCurrentAppVersion,
  getErrorMessage,
  SLHeaders,
} from '@travelwin/core';
import { v4 as uuidv4 } from 'uuid';

import { forceSignOut, getAuthToken } from '../../context/User';
import { getCurrentFlavoredLocale } from '../../i18n';
import {
  apiCompatibilityChecker,
  checkVersionForWebOnly,
} from './eCommerceVersionCheck';
import { logHttp } from './logger';
import { getApiUrl, getAppOrigin } from './requests';

export class HttpError extends Error {
  constructor(
    public path: string,
    public options: RequestInit | undefined,
    public status: number,
    public body: any,
    message?: string,
  ) {
    super(message);
  }

  public isNotFound() {
    return this.status === 404;
  }
}

export const isHttpError = (error: unknown): error is HttpError => {
  return error instanceof HttpError;
};

export const isNotFoundError = (error: unknown): boolean => {
  return isHttpError(error) && error.isNotFound();
};

export const request = async (
  path: string,
  options?: RequestInit,
  jwtToken?: string,
) => {
  const startTime = performance.now();

  let accessToken = jwtToken;

  if (!accessToken) {
    try {
      const jwtToken = await getAuthToken();
      accessToken = jwtToken?.toString();
    } catch (e) {
      return e;
    }
  }

  const correlationId = uuidv4();
  const requestId = uuidv4();

  const requestInit: RequestInit = {
    ...options,
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(typeof window === 'undefined'
        ? { Origin: process.env.REACT_APP_CONFIG_ORIGIN }
        : {}),
      [SLHeaders.LOCALE]: getCurrentFlavoredLocale(),
      [SLHeaders.APP_ORIGIN]: getAppOrigin(),
      [SLHeaders.APP_VERSION]: getCurrentAppVersion(),
      [SLHeaders.CORRELATION_ID]: correlationId,
      [SLHeaders.REQUEST_ID]: requestId,

      ...options?.headers,
    },
  };

  const basePath = getApiUrl() || '/';

  const response = await fetch(`${basePath}${path}`, requestInit);

  logHttp({
    url: path,
    requestInit,
    response,
    startTime,
  });

  checkVersionForWebOnly(response.headers.get(API_VERSION_HEADER));

  if (response.status === 401) {
    await forceSignOut();
    window.location.reload();
    return;
  }

  let body;
  try {
    body = await response.json();
  } catch {
    /* empty */
  }

  apiCompatibilityChecker.check(response.status, body);

  if (response.ok) {
    return body;
  }

  throw new HttpError(
    path,
    requestInit,
    response.status,
    body,
    getErrorMessage(body),
  );
};
